/**
 * Plan template types — the core abstractions for the multi-plan coach system.
 *
 * PlanTemplate + StopBuilder drive buildCoachPlan(). All types live here so that
 * stop-builder modules and the engine itself share a single dependency chain with
 * no circular imports.
 */

import { type Item, type CurriculumNode } from "./item-library.js";
import { type CardStore, type MasteryThresholds } from "./scheduler.js";
import { type ReviewSummary } from "./skill-state.js";

// ─── output types ─────────────────────────────────────────────────────────────

export type SessionMix = "review-only" | "balanced" | "new-focus";

/** Per-node acquisition status attached to the Introduce stop. */
export interface PlanStopNodeAcquisition {
  nodeId: string;
  /** "needs-listen" until exposure threshold is met; "ready" once cleared. */
  status: "needs-listen" | "ready";
  /** Total Listen + Drill plays recorded for this node so far. */
  exposures: number;
  /** Minimum exposures required to unlock graded Practice for this node. */
  threshold: number;
}

export type WellKnownStopKind = "strengthen" | "review" | "introduce" | "maintenance";
// Allows future generators to emit custom stop kinds while preserving autocomplete.
export type PlanStopKind = WellKnownStopKind | (string & NonNullable<unknown>);

export interface PlanStop {
  kind: PlanStopKind;
  title: string;
  reason: string;
  itemCount: number;
  estimatedMinutes: number;
  items: Item[];
  /** Introduce stop: open Listen mode in the session runner before graded practice. */
  listenFirst?: boolean;
  /** Introduce stop: drill route for the Listen-first interstitial link. */
  drillRoute?: string;
  /** Introduce stop: per-node acquisition progress. Present when nodeExposures is passed. */
  nodeAcquisition?: readonly PlanStopNodeAcquisition[];
  /** Strengthen stop: true when the node's 7-day lapse rate is ≥ 40%. Suggests re-listening. */
  suggestRelisten?: boolean;
  /** Strengthen stop: drill listen route for the re-listen nudge link. */
  suggestRelistenRoute?: string;
}

export interface CoachPlan {
  stops: PlanStop[];
  totalEstimatedMinutes: number;
  totalItemCount: number;
  /** "standard" always in Phase 1; "intervention" when the analyzer overrides the template (Phase 2). */
  mode: "standard" | "intervention";
  /** Populated only when mode === "intervention". */
  interventionNodeTitle?: string;
}

// ─── config ───────────────────────────────────────────────────────────────────

export interface PlanConfig {
  warmupItemCap: number;
  newItemCap: number;
  warmupLapseThreshold: number;
  warmupLapseMinCount: number;
  relistenLapseThreshold: number;
  lapseWindowMs: number;
  reviewBudgetFraction: number;
  weakSpotMinReps: number;
  minNodeEngagement: number;
  minPracticeReviewsPerNode: number;
  /**
   * Maximum items surfaced by the maintenance fallback stop — a small set
   * of the lowest-retrievability items shown when all primary stops are empty.
   * The maintenance stop fires regardless of sessionMix.
   */
  maintenanceItemCap: number;
  /**
   * How far ahead (in ms) to pull items that are not yet due but approaching
   * their due date. Prevents empty-plan days when FSRS intervals are longer
   * than 1 day. Items within this window are included in the Review stop,
   * sorted by lowest retrievability (most-at-risk first).
   * Default: 2 days. Set to 0 to enforce strict due-only scheduling.
   */
  reviewLookAheadMs: number;
  /** Phase 2 — intervention trigger thresholds. Present on all configs to avoid optional access. */
  interventionLapseThreshold: number;
  interventionMinAttempts: number;
  /** Skip prerequisite-mastery gate (Filter A) for developer-authored curated paths. */
  bypassFilterA?: boolean;
  /** Skip minimum-engagement gate (Filter B) for developer-authored curated paths. */
  bypassFilterB?: boolean;
  /** Skip practice-review-count gate (Filter C) for developer-authored curated paths. */
  bypassFilterC?: boolean;
}

// ─── engine types ─────────────────────────────────────────────────────────────

/** All data available to stop builders, computed once per plan invocation. */
export interface PlanBuildData {
  items: Item[];
  cards: CardStore;
  thresholds: MasteryThresholds;
  reviews: readonly ReviewSummary[];
  sessionTargetMinutes: number;
  sessionMix: SessionMix;
  newSkillNodeId: string | null;
  nodeExposures: ReadonlyMap<string, number>;
  /**
   * Tester override: when true, every introduced node is treated as acquired
   * ("ready"), skipping the Listen-first / bypass-quiz acquisition gate. Driven
   * by the `unlockEverything` pref. Never alters card state.
   */
  forceAcquired: boolean;
  now: Date;
  nodes: CurriculumNode[];
  unlockedNodeSet: ReadonlySet<string>;
  engagedNodeSet: ReadonlySet<string>;
  practiceReviewedNodeSet: ReadonlySet<string>;
}

/**
 * A stop builder is a pure function — no I/O, no side effects.
 * Returns a PlanStop or null (stop omitted when null).
 */
export type StopBuilder = (
  data: PlanBuildData,
  config: PlanConfig,
  alreadyPlanned: ReadonlySet<string>,
) => PlanStop | null;

export interface PlanTemplate {
  id: string;
  label: string;
  description: string;
  /** In-scope curriculum subset. Drives all three filter computations. */
  nodes: CurriculumNode[];
  /** Stop builders executed in order; null results are skipped. */
  stops: StopBuilder[];
  config: PlanConfig;
  /** Per-template acquisition threshold overrides. Unused in Phase 1. */
  acquisitionThresholds?: ReadonlyMap<string, number>;
}

// ─── shared constant ──────────────────────────────────────────────────────────

/** Items-per-minute estimate used to convert item counts ↔ minutes. */
export const ITEMS_PER_MINUTE = 6;
